import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';

// Define webhook configurations based on source
// Supported sources: com, web, sp, mkt, legal, hr
const webhookConfig = {
  com: {
    url: 'https://n8n.margue.net:5678/webhook/landxcom',  // Company webhook
    title: 'Company Chat',
    auth: true,
  },
  web: {
    url: 'https://n8n.margue.net:5678/webhook/landxweb',  // Web source webhook
    title: 'Web Support',
    auth: true,
  },
  sp: {
    url: 'https://n8n.margue.net:5678/webhook/landxsp',   // Support webhook
    title: 'Support Chat',
    auth: true,
  },
  mkt: {
    url: 'https://n8n.margue.net:5678/webhook/landxmkt',  // Marketing webhook
    title: 'Marketing Chat',
    auth: true,
  },
  legal: {
    url: 'https://n8n.margue.net:5678/webhook/landxlegal', // Legal webhook
    title: 'Legal Assistant',
    auth: true,
  },
  hr: {
    url: 'https://n8n.margue.net:5678/webhook/landxhr',    // HR webhook
    title: 'HR Assistant',
    auth: true,
  },
  aicitysp: {
    url: 'https://n8n.margue.net:5678/webhook/acqdrant',    // AI City webhook
    title: 'AI City Assistant',
    auth: true,
  },
};

// Default configuration if source is not recognized
const defaultConfig = {
  url: 'https://n8n.margue.net:5678/webhook/acqdrant',
  title: 'AI City Chat',
  auth: true,
};

const Chatbot = () => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [config, setConfig] = useState(defaultConfig);
  const [jwtToken, setJwtToken] = useState(null); // Should be set via environment or props, not UI
  const [source, setSource] = useState('default'); // Use default config when no source is specified
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Detect if running in iframe or embedded mode
  const [isEmbedded, setIsEmbedded] = useState(false);

  // Extract source from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sourceParam = urlParams.get('source'); // Don't default to 'com' here
    const embedParam = urlParams.get('embedded'); // Check for embedded parameter

    // Check if we're in an iframe or embedded mode is explicitly set
    const inIframe = window.self !== window.top || embedParam === 'true';
    setIsEmbedded(inIframe);

    let configToUse;

    // If no source parameter is provided, use default config
    if (!sourceParam) {
      setSource('default');
      configToUse = defaultConfig;
      setConfig(defaultConfig);
    } else {
      setSource(sourceParam);
      configToUse = webhookConfig[sourceParam] || defaultConfig;
      setConfig(configToUse);
    }

    // Add welcome message based on the source
    setMessages([
      {
        id: '1',
        text: `Hello! Welcome to the ${configToUse.title}. How can I help you today?`,
        sender: 'bot',
        timestamp: new Date(),
      }
    ]);
  }, []);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Scroll to bottom of messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    // Add user message to chat
    const userMessage = {
      id: Date.now().toString(),
      text: inputText,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      // Prepare conversation history for context
      const conversationHistory = messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text,
        timestamp: msg.timestamp.toISOString(),
      }));

      // Prepare request to webhook with full context
      const requestBody = {
        message: inputText,
        timestamp: new Date().toISOString(),
        source: source,
        history: conversationHistory, // Add conversation history for context
      };

      // Prepare headers
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add JWT token for authentication
      // Token should be configured via environment or build-time configuration
      // For development/testing, you can set it as a global variable window.JWT_TOKEN
      const authToken = process.env.REACT_APP_JWT_TOKEN || window.JWT_TOKEN;
      if (config.auth && authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      // Add debugging - you can check this in browser console
      console.log('Sending request to:', config.url);
      console.log('Authorization header:', headers['Authorization']);
      console.log('Request body:', requestBody);

      // Send request to the appropriate webhook
      console.log('About to send fetch request...');
      const response = await fetch(config.url, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      });

      // Log response status for debugging - this will execute regardless of success/failure
      console.log('Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        // Log the error response body for debugging
        const errorText = await response.text();
        console.error('Webhook error response:', errorText);
        throw new Error(`Webhook error: ${response.status} ${response.statusText} - ${errorText}`);
      }

      // Log the raw response for debugging
      const responseText = await response.text();
      console.log('Raw response from webhook:', responseText);
      
      let responseData;
      try {
        responseData = JSON.parse(responseText);
        console.log('Parsed JSON response:', responseData);
      } catch (e) {
        console.error('Error parsing JSON response:', e);
        responseData = responseText; // Use raw text if JSON parsing fails
      }

      // Handle different response formats from the webhook
      let botResponseText = null;

      // Check if response has the expected format from your webhook
      // Try multiple common response formats
      if (Array.isArray(responseData) && responseData.length > 0) {
        // Array format - check for various properties
        if (responseData[0].output) {
          botResponseText = responseData[0].output;
          console.log('Found output in array format:', botResponseText);
        } else if (responseData[0].message) {
          botResponseText = responseData[0].message;
          console.log('Found message in array format:', botResponseText);
        } else if (responseData[0].text) {
          botResponseText = responseData[0].text;
          console.log('Found text in array format:', botResponseText);
        } else if (typeof responseData[0] === 'string') {
          botResponseText = responseData[0];
          console.log('Found string in array format:', botResponseText);
        }
      } else if (responseData && typeof responseData === 'object') {
        // Object format - check for various properties
        if (typeof responseData.response === 'string') {
          botResponseText = responseData.response;
          console.log('Found response property:', botResponseText);
        } else if (typeof responseData.output === 'string') {
          botResponseText = responseData.output;
          console.log('Found output property:', botResponseText);
        } else if (typeof responseData.message === 'string') {
          botResponseText = responseData.message;
          console.log('Found message property:', botResponseText);
        } else if (typeof responseData.text === 'string') {
          botResponseText = responseData.text;
          console.log('Found text property:', botResponseText);
        }
      } else if (typeof responseData === 'string') {
        botResponseText = responseData;
        console.log('Using raw string response:', botResponseText);
      }

      // If we still don't have a response, log the structure and use fallback
      if (!botResponseText) {
        console.warn('Could not parse response. Response structure:', JSON.stringify(responseData, null, 2));
        botResponseText = 'I received your message. How else can I help you?';
      }

      console.log('Final bot response text:', botResponseText);

      // Add bot response to chat
      const botMessage = {
        id: (Date.now() + 1).toString(),
        text: botResponseText,
        sender: 'bot',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        text: `Sorry, I encountered an error processing your request: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`,
        sender: 'bot',
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // JWT token can be set via environment variable or props
  // For security reasons, there's no UI to set the token

  return (
    <div className="chatbot-container" style={{
      display: 'flex',
      flexDirection: 'column',
      height: isEmbedded ? '100vh' : '85vh',
      width: isEmbedded ? '100%' : '90%',
      maxWidth: isEmbedded ? 'none' : '900px',
      margin: isEmbedded ? '0' : '0 auto',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      marginTop: isEmbedded ? '0' : '4vh',
      borderRadius: isEmbedded ? '0' : '20px',
      boxShadow: isEmbedded ? 'none' : '0 20px 60px rgba(0,0,0,0.15), 0 0 1px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      background: 'white',
    }}>
      <div className="chatbot-header" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '20px 24px',
        textAlign: 'center',
        fontSize: '20px',
        fontWeight: '600',
        letterSpacing: '0.3px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
          <span style={{ fontSize: '24px' }}>💬</span>
          <span>{config.title}</span>
        </div>
        <div style={{
          marginTop: '8px',
          fontSize: '11px',
          opacity: '0.85',
          fontWeight: '400',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          {source !== 'default' && `${source} •`} AI Assistant
        </div>
      </div>
      
      <div className="chatbot-messages" style={{
        flex: 1,
        padding: '24px',
        overflowY: 'auto',
        background: 'linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%)',
        minHeight: '0',
      }}>
        {messages.map((message) => (
          <div
            key={message.id}
            style={{
              marginBottom: '16px',
              textAlign: message.sender === 'user' ? 'right' : 'left',
              display: 'flex',
              flexDirection: 'column',
              alignItems: message.sender === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <div
              style={{
                display: 'inline-block',
                padding: '12px 18px',
                borderRadius: message.sender === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                background: message.sender === 'user'
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'white',
                color: message.sender === 'user' ? 'white' : '#2d3748',
                maxWidth: '75%',
                wordWrap: 'break-word',
                textAlign: 'left',
                boxShadow: message.sender === 'user'
                  ? '0 4px 12px rgba(102, 126, 234, 0.3)'
                  : '0 2px 8px rgba(0,0,0,0.08)',
                border: message.sender === 'bot' ? '1px solid #e2e8f0' : 'none',
                fontSize: '15px',
                lineHeight: '1.5',
              }}
            >
              {message.sender === 'bot' ? (
                <ReactMarkdown
                  components={{
                    // Style paragraphs
                    p: ({node, ...props}) => <p style={{ margin: '0 0 10px 0', lineHeight: '1.5' }} {...props} />,
                    // Style links
                    a: ({node, ...props}) => {
                      const href = props.href || '';
                      return (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#667eea',
                            textDecoration: 'none',
                            fontWeight: '600',
                            borderBottom: '2px solid rgba(102, 126, 234, 0.3)',
                            transition: 'all 0.2s ease',
                          }}
                          title={href}
                          onMouseEnter={(e) => {
                            e.target.style.color = '#764ba2';
                            e.target.style.borderBottomColor = '#764ba2';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = '#667eea';
                            e.target.style.borderBottomColor = 'rgba(102, 126, 234, 0.3)';
                          }}
                        >
                          {props.children}
                        </a>
                      );
                    },
                    // Style lists
                    ul: ({node, ...props}) => <ul style={{ margin: '8px 0', paddingLeft: '20px' }} {...props} />,
                    li: ({node, ...props}) => <li style={{ margin: '4px 0', lineHeight: '1.4' }} {...props} />,
                    // Style strong/bold text
                    strong: ({node, ...props}) => <strong style={{ fontWeight: '700', color: '#2c3e50' }} {...props} />,
                    // Style headings
                    h1: ({node, ...props}) => <h1 style={{ fontSize: '1.2em', fontWeight: 'bold', margin: '10px 0 8px 0' }} {...props} />,
                    h2: ({node, ...props}) => <h2 style={{ fontSize: '1.1em', fontWeight: 'bold', margin: '10px 0 8px 0' }} {...props} />,
                    h3: ({node, ...props}) => <h3 style={{ fontSize: '1.05em', fontWeight: 'bold', margin: '8px 0 6px 0' }} {...props} />,
                  }}
                >
                  {message.text}
                </ReactMarkdown>
              ) : (
                message.text
              )}
            </div>
            <div
              style={{
                fontSize: '11px',
                color: '#a0aec0',
                marginTop: '6px',
                marginLeft: message.sender === 'user' ? '0' : '8px',
                marginRight: message.sender === 'user' ? '8px' : '0',
                fontWeight: '500',
              }}
            >
              {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        ))}
        {isLoading && (
          <div style={{ textAlign: 'left', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 18px',
                borderRadius: '20px 20px 20px 4px',
                background: 'white',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              <div style={{ display: 'flex', gap: '4px' }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#667eea',
                  animation: 'bounce 1.4s infinite ease-in-out both',
                  animationDelay: '-0.32s'
                }}></div>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#667eea',
                  animation: 'bounce 1.4s infinite ease-in-out both',
                  animationDelay: '-0.16s'
                }}></div>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#667eea',
                  animation: 'bounce 1.4s infinite ease-in-out both'
                }}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      <div className="chatbot-input" style={{
        padding: '20px 24px',
        backgroundColor: 'white',
        borderTop: '1px solid #e2e8f0',
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
      }}>
        <input
          ref={inputRef}
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your message here..."
          disabled={isLoading}
          style={{
            flex: 1,
            padding: '14px 20px',
            border: '2px solid #e2e8f0',
            borderRadius: '30px',
            outline: 'none',
            fontSize: '15px',
            transition: 'all 0.2s ease',
            backgroundColor: '#f8f9fa',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#667eea';
            e.target.style.backgroundColor = 'white';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e2e8f0';
            e.target.style.backgroundColor = '#f8f9fa';
          }}
        />
        <button
          onClick={handleSendMessage}
          disabled={!inputText.trim() || isLoading}
          style={{
            padding: '14px 28px',
            background: inputText.trim() && !isLoading
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : '#cbd5e0',
            color: 'white',
            border: 'none',
            borderRadius: '30px',
            cursor: inputText.trim() && !isLoading ? 'pointer' : 'not-allowed',
            fontSize: '15px',
            fontWeight: '600',
            transition: 'all 0.2s ease',
            boxShadow: inputText.trim() && !isLoading
              ? '0 4px 12px rgba(102, 126, 234, 0.3)'
              : 'none',
          }}
          onMouseEnter={(e) => {
            if (inputText.trim() && !isLoading) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = inputText.trim() && !isLoading
              ? '0 4px 12px rgba(102, 126, 234, 0.3)'
              : 'none';
          }}
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default Chatbot;